import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Save, Trash2, Tag } from 'lucide-react';
import { toast } from 'sonner';
import EmptyState from '@/components/EmptyState';
import { Checkbox } from '@/components/ui/checkbox';
import useBulkSelect, { bulkDelete } from '@/hooks/useBulkSelect';
import BulkDeleteBar from '@/components/BulkDeleteBar';

export default function InstrumentTypesPage() {
  const [types, setTypes] = useState([]);
  const [selectedName, setSelectedName] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [nameForm, setNameForm] = useState({});
  const [typeForm, setTypeForm] = useState({ typeName: '', note: '' });

  const load = () => api.get('/instrument-types').then(r => setTypes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const selectName = (t) => { setSelectedName(t.id); setNameForm({ ...t }); setSelectedType(null); };

  const createName = async () => {
    try {
      const res = await api.post('/instrument-types', { name: 'Nový přístroj' });
      load();
      selectName(res.data);
      toast.success('Vytvořeno');
    } catch { toast.error('Chyba'); }
  };

  const saveName = async () => {
    if (!selectedName) return;
    try {
      await api.put(`/instrument-types/${selectedName}`, nameForm);
      load();
      toast.success('Uloženo');
    } catch { toast.error('Chyba'); }
  };

  const addType = () => {
    if (!typeForm.typeName) return;
    const updatedTypes = [...(nameForm.types || []), { ...typeForm }];
    setNameForm({ ...nameForm, types: updatedTypes });
    setTypeForm({ typeName: '', note: '' });
  };

  const removeType = (idx) => {
    const updatedTypes = nameForm.types.filter((_, i) => i !== idx);
    setNameForm({ ...nameForm, types: updatedTypes });
  };

  const deleteName = async () => {
    if (!selectedName || !window.confirm('Smazat?')) return;
    await api.delete(`/instrument-types/${selectedName}`);
    setSelectedName(null);
    load();
  };

  const currentItem = types.find(t => t.id === selectedName);

  const sel = useBulkSelect(types.map(t => t.id));

  const bulkRemove = async () => {
    const ids = sel.selectedIds;
    const { ok, failed } = await bulkDelete(ids, id => api.delete(`/instrument-types/${id}`));
    if (selectedName && ids.includes(selectedName)) { setSelectedName(null); }
    sel.clear();
    load();
    if (failed) toast.warning(`Smazáno ${ok}, ${failed} selhalo`);
    else toast.success(`Smazáno ${ok}`);
  };

  return (
    <div data-testid="instrument-types-page" className="flex gap-4 h-[calc(100vh-7rem)]">
      {/* Col 1: Names */}
      <div className="ct-panel w-64 flex-shrink-0 flex flex-col">
        <div className="ct-panel-header flex items-center justify-between">
          <span>Název přístroje</span>
          <button data-testid="add-instrument-type-btn" onClick={createName} className="p-1 rounded" style={{ color: 'var(--ct-accent)' }}>
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {types.length > 0 && (
          <div className="p-2 space-y-2">
            <label className="flex items-center gap-2 px-1 text-xs cursor-pointer" style={{ color: 'var(--ct-text-secondary)' }}>
              <Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} aria-label="Vybrat vše" />
              Vybrat vše
            </label>
            <BulkDeleteBar count={sel.count} onDelete={bulkRemove} onClear={sel.clear} entityLabel="typů přístrojů" />
          </div>
        )}
        <ScrollArea className="flex-1">
          {types.map(t => (
            <div
              key={t.id}
              className={`ct-table-row flex items-center gap-2 px-3 py-2 text-sm ${selectedName === t.id ? 'active' : ''}`}
            >
              <Checkbox
                checked={sel.isSelected(t.id)}
                onCheckedChange={() => sel.toggle(t.id)}
                aria-label={`Vybrat ${t.name}`}
              />
              <button onClick={() => selectName(t)} className="flex-1 text-left">
                {t.name}
              </button>
            </div>
          ))}
        </ScrollArea>
      </div>

      {/* Col 2: Detail + Types list */}
      {selectedName ? (
        <div className="ct-panel flex-1 flex flex-col">
          <div className="ct-panel-header flex items-center justify-between">
            <span>Detail vybraného názvu</span>
            <button onClick={deleteName} className="p-1 rounded hover:bg-[var(--ct-border)]" style={{ color: 'var(--ct-error)' }}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <div className="ct-form-field">
              <label className="ct-form-label">Název přístroje</label>
              <Input value={nameForm.name || ''} onChange={e => setNameForm({ ...nameForm, name: e.target.value })} className="h-9 text-sm" />
            </div>
            <Button onClick={saveName} size="sm" className="ct-btn-primary gap-1">
              <Save className="w-3 h-3" /> Uložit
            </Button>
          </div>
          <div className="border-t flex-1 flex flex-col" style={{ borderColor: 'var(--ct-border)' }}>
            <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--ct-text-secondary)' }}>
              Typy přístroje ({(nameForm.types || []).length})
            </div>
            <ScrollArea className="flex-1 px-4">
              {(nameForm.types || []).map((t, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedType(idx)}
                  className={`ct-table-row w-full text-left px-3 py-2 text-sm rounded mb-1 ${selectedType === idx ? 'active' : ''}`}
                  style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)' }}
                >
                  <div className="flex justify-between">
                    <span className="font-mono">{t.typeName}</span>
                    <button onClick={(e) => { e.stopPropagation(); removeType(idx); }} style={{ color: 'var(--ct-error)' }}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </button>
              ))}
            </ScrollArea>
            <div className="p-4 border-t" style={{ borderColor: 'var(--ct-border)' }}>
              <div className="flex gap-2">
                <Input placeholder="Typ přístroje" value={typeForm.typeName} onChange={e => setTypeForm({ ...typeForm, typeName: e.target.value })} className="h-8 text-sm" />
                <Input placeholder="Poznámka" value={typeForm.note} onChange={e => setTypeForm({ ...typeForm, note: e.target.value })} className="h-8 text-sm" />
                <Button onClick={addType} size="sm" className="ct-btn-primary h-8 px-2">
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={Tag}
            title="Vyberte název přístroje"
            description="Klikněte na název v seznamu vlevo pro přidávání nebo úpravu typů."
            size="sm"
          />
        </div>
      )}

      {/* Col 3: Type detail */}
      {selectedType !== null && nameForm.types?.[selectedType] ? (
        <div className="ct-panel w-72 flex-shrink-0">
          <div className="ct-panel-header">Detail vybraného typu</div>
          <div className="p-4 space-y-3">
            <div className="ct-form-field">
              <label className="ct-form-label">Typ přístroje</label>
              <div className="font-mono text-sm p-2 rounded" style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)' }}>
                {nameForm.types[selectedType].typeName}
              </div>
            </div>
            <div className="ct-form-field">
              <label className="ct-form-label">Poznámka</label>
              <div className="text-sm p-2 rounded" style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', minHeight: '3rem' }}>
                {nameForm.types[selectedType].note || '—'}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
